import { DE_ADMIN, roles } from '@/constants';

/**
 * Pure helpers for interpreting Auth0 `user_metadata` and DE Auth API responses.
 * No stores, no routing, no side effects — see src/composables/auth/useSession.js
 * for what the app actually does with the values derived here.
 */

const isDeAdmin = (userMetadata) => !!userMetadata?.[DE_ADMIN];

export function deriveAuth0Role(userMetadata, cachedRole = '') {
    if (isDeAdmin(userMetadata)) return roles.DE_ROLE;

    const { groupAdminId, groups } = extractGroupAdminMetadata(userMetadata);
    if (groupAdminId && groups) return roles.GROUPADMIN_ROLE;

    const { organizations, employeeId } = extractEmployeeMetadata(userMetadata);
    if (employeeId && organizations) {
        const volunteersOrgId = extractFirstOrgId(organizations);
        const isAdmin = isVolunteerAnAdmin(organizations, volunteersOrgId);

        // Only set to org admin if the cache previously was set to org admin.
        // Logic later on verifies this anyways.
        if (isAdmin && cachedRole === roles.ORGADMIN_ROLE) return roles.ORGADMIN_ROLE;
        return roles.EMPLOYEE_ROLE;
    }

    return undefined;
}

export function deriveEducatorRole(userRoles) {
    return isStudentRole(userRoles) ? roles.STUDENT_ROLE : roles.EDUCATOR_ROLE;
}

export function parseOrganizations(organizations = {}) {
    const orgIds = Object.keys(organizations).filter((id) => id.toLowerCase() !== 'none');

    return orgIds.map((organizationId) => ({
        organizationId,
        roles: organizations[organizationId],
    }));
}

export function extractGroupAdminMetadata(userMetadata) {
    const { group_admin_id: groupAdminId, groups } = userMetadata ?? {};
    return { groupAdminId, groups };
}

export function extractEmployeeMetadata(userMetadata) {
    const { employee_id: employeeId, organizations } = userMetadata ?? {};
    return { employeeId, organizations, parsedOrganizations: parseOrganizations(organizations) };
}

export function isVolunteerAnAdmin(organizationsById, organizationId) {
    const targetOrganization = organizationsById?.[organizationId] ?? [];

    const replaceSpacesWithDashes = (str) => str.replace(/\s+/g, '-');
    const orgRoles = targetOrganization.map((each) => replaceSpacesWithDashes(each).toLowerCase());
    return orgRoles.includes(roles.ORGADMIN_ROLE);
}

export function extractFirstOrgId(organizations) {
    const orgKeys = Object.keys(organizations ?? {});
    return orgKeys.length > 0 ? orgKeys[0] : null;
}

const isStudentRole = (userRoles) => !!userRoles?.some((role) => role.code === 'STUDENT');
